import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import { 
  Play, 
  Pause, 
  Trash2, 
  RefreshCw,
  Clock,
  Loader2,
  CheckCircle,
  XCircle,
  AlertTriangle
} from 'lucide-react';
import { useState } from 'react';

export function QueuesPage() {
  const queryClient = useQueryClient();

  const { data: queueStats, isLoading, refetch } = useQuery({
    queryKey: ['queue-stats'],
    queryFn: () => api.getQueueStats(),
    refetchInterval: 5000, // Refresh every 5 seconds
  });

  const pauseMutation = useMutation({
    mutationFn: (queueName: string) => api.pauseQueue(queueName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['queue-stats'] });
    },
  });

  const resumeMutation = useMutation({
    mutationFn: (queueName: string) => api.resumeQueue(queueName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['queue-stats'] });
    },
  });

  const cleanMutation = useMutation({
    mutationFn: (queueName: string) => api.cleanQueue(queueName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['queue-stats'] });
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  const queues = queueStats?.data;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <p className="text-slate-400">Monitor and manage notification queues</p>
        <button 
          onClick={() => refetch()}
          className="btn btn-secondary flex items-center gap-2"
        >
          <RefreshCw className="h-5 w-5" />
          Refresh
        </button>
      </div>

      {/* Queue Cards */}
      <div className="grid gap-6">
        <QueueCard
          name="Transactional"
          description="High-priority notifications (order confirmations, alerts)"
          stats={queues?.transactional}
          color="blue"
          onPause={() => pauseMutation.mutate('transactional')}
          onResume={() => resumeMutation.mutate('transactional')}
          onClean={() => cleanMutation.mutate('transactional')}
          isLoading={pauseMutation.isPending || resumeMutation.isPending || cleanMutation.isPending}
        />
        <QueueCard
          name="Marketing"
          description="Bulk marketing notifications (batched delivery)"
          stats={queues?.marketing}
          color="purple"
          onPause={() => pauseMutation.mutate('marketing')}
          onResume={() => resumeMutation.mutate('marketing')}
          onClean={() => cleanMutation.mutate('marketing')}
          isLoading={pauseMutation.isPending || resumeMutation.isPending || cleanMutation.isPending}
        />
        <QueueCard
          name="Cleanup"
          description="Scheduled cleanup tasks (old data removal)"
          stats={queues?.cleanup}
          color="yellow"
          onPause={() => pauseMutation.mutate('cleanup')}
          onResume={() => resumeMutation.mutate('cleanup')}
          onClean={() => cleanMutation.mutate('cleanup')}
          isLoading={pauseMutation.isPending || resumeMutation.isPending || cleanMutation.isPending}
        />
      </div>

      {/* Legend */}
      <div className="card">
        <h3 className="text-sm font-medium text-slate-300 mb-3">Job Status Legend</h3>
        <div className="flex flex-wrap gap-6">
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-slate-400" />
            <span className="text-sm text-slate-400">Waiting - Jobs queued for processing</span>
          </div>
          <div className="flex items-center gap-2">
            <Loader2 className="h-4 w-4 text-blue-400" />
            <span className="text-sm text-slate-400">Active - Currently being processed</span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4 text-green-400" />
            <span className="text-sm text-slate-400">Completed - Successfully processed</span>
          </div>
          <div className="flex items-center gap-2">
            <XCircle className="h-4 w-4 text-red-400" />
            <span className="text-sm text-slate-400">Failed - Processing failed</span>
          </div>
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-yellow-400" />
            <span className="text-sm text-slate-400">Delayed - Scheduled for later</span>
          </div>
        </div>
      </div>
    </div>
  );
}

interface QueueCardProps {
  name: string;
  description: string;
  stats?: {
    waiting: number;
    active: number;
    completed: number;
    failed: number;
    delayed: number;
    paused: number;
  };
  color: 'blue' | 'purple' | 'yellow';
  onPause: () => void;
  onResume: () => void;
  onClean: () => void;
  isLoading: boolean;
}

function QueueCard({ name, description, stats, color, onPause, onResume, onClean, isLoading }: QueueCardProps) {
  const [isPaused, setIsPaused] = useState(false);

  const colors = {
    blue: 'border-blue-500',
    purple: 'border-purple-500',
    yellow: 'border-yellow-500',
  };

  const bgColors = {
    blue: 'bg-blue-500/10',
    purple: 'bg-purple-500/10',
    yellow: 'bg-yellow-500/10',
  };

  const handlePauseToggle = () => {
    if (isPaused) {
      onResume();
      setIsPaused(false);
    } else {
      onPause();
      setIsPaused(true);
    }
  };

  const handleClean = () => {
    if (confirm(`Are you sure you want to clean the ${name} queue? This will remove all completed and failed jobs.`)) {
      onClean();
    }
  };

  const total = (stats?.waiting || 0) + (stats?.active || 0) + (stats?.delayed || 0);

  return (
    <div className={`card border-l-4 ${colors[color]}`}>
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-white">{name} Queue</h3>
          <p className="text-sm text-slate-400">{description}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handlePauseToggle}
            disabled={isLoading}
            className={`btn ${isPaused ? 'btn-success' : 'btn-secondary'} p-2`}
            title={isPaused ? 'Resume' : 'Pause'}
          >
            {isPaused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
          </button>
          <button
            onClick={handleClean}
            disabled={isLoading}
            className="btn btn-danger p-2"
            title="Clean queue"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <StatItem
          label="Waiting"
          value={stats?.waiting || 0}
          icon={Clock}
          color="slate"
        />
        <StatItem
          label="Active"
          value={stats?.active || 0}
          icon={Loader2}
          color="blue"
          animate={!!stats?.active}
        />
        <StatItem
          label="Completed"
          value={stats?.completed || 0}
          icon={CheckCircle}
          color="green"
        />
        <StatItem
          label="Failed"
          value={stats?.failed || 0}
          icon={XCircle}
          color="red"
        />
        <StatItem
          label="Delayed"
          value={stats?.delayed || 0}
          icon={AlertTriangle}
          color="yellow"
        />
      </div>

      {/* Progress indicator */}
      {total > 0 && (
        <div className="mt-4 pt-4 border-t border-slate-700">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-slate-400">Queue Load</span>
            <span className="text-white">{total} jobs pending</span>
          </div>
          <div className={`h-2 rounded-full ${bgColors[color]}`}>
            <div 
              className={`h-full rounded-full ${colors[color].replace('border', 'bg')}`}
              style={{ width: `${Math.min((stats?.active || 0) / Math.max(total, 1) * 100, 100)}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

interface StatItemProps {
  label: string;
  value: number;
  icon: React.ComponentType<{ className?: string }>;
  color: 'slate' | 'blue' | 'green' | 'red' | 'yellow';
  animate?: boolean;
}

function StatItem({ label, value, icon: Icon, color, animate }: StatItemProps) {
  const colors = {
    slate: 'text-slate-400',
    blue: 'text-blue-400',
    green: 'text-green-400',
    red: 'text-red-400',
    yellow: 'text-yellow-400',
  };

  return (
    <div className="bg-slate-700/50 rounded-lg p-3 text-center">
      <Icon className={`h-5 w-5 mx-auto mb-1 ${colors[color]} ${animate ? 'animate-spin' : ''}`} />
      <p className={`text-2xl font-bold ${colors[color]}`}>{value}</p>
      <p className="text-xs text-slate-500">{label}</p>
    </div>
  );
}
